import { makeAutoObservable, runInAction } from "mobx";
import * as api from "../api";

class AccountStore {
    _id = null;
    account = null;
    // Kept as an empty array rather than removed: no consumer of this store reads it
    // (verified across every getAccountStore call site), but if one is ever missed it
    // gets an empty list instead of `undefined` blowing up on `.map`/`.length`.
    events = [];
    isLoading = true;
    allCoupons = {};
    fetchingCoupons = false;

    constructor(_id) {
        makeAutoObservable(this);
        this._id = _id;
    }

    get productionCoupons() {
        return Object.values(this.allCoupons).filter(coupon => !coupon.event);
    }

    getCoupon(couponId) {
        return this.allCoupons[couponId];
    }

    updateCoupon(coupon) {
        runInAction(() => {
            this.allCoupons[coupon._id] = coupon;
        });
    }

    async deleteCoupon(couponId) {
        await api.deleteCoupon({ accountId: this._id, couponId });
        runInAction(() => delete this.allCoupons[couponId]);
    }

    async fetchProductionCoupons() {
        try {
            this.fetchingCoupons = true;
            const { data } = await api.getCouponsByAccount({ accountId: this._id });
            data.forEach(c => this.allCoupons[c._id] = c);
        } catch (e) {
            console.error(e);
        } finally {
            runInAction(() => this.fetchingCoupons = false);
        }
    }

    // `getAccountEvents` used to be fetched here too, purely to keep `this.events`
    // as a list of ids — a field nothing ever read. Every page that mounts this store
    // (EventsPage, CouponsPage, ProductionSettings) sits behind AllEventsGuard, which
    // already fetches the same endpoint into DashboardContext, so this was a second
    // identical request per page load. On a production-sized account that response is
    // ~255KB and the call has been observed taking >20s, all of it discarded except
    // seventy ids. Read events from `eventContext` if this store ever needs them.
    async fetchData() {
        this.isLoading = true;
        const promises = [
            api.getAccountSecured(this._id),
            this.fetchProductionCoupons()
        ];
        const [account] = await Promise.all(promises);
        this.account = account.data;
        runInAction(() => this.isLoading = false);
    }

    async updateAccount(accountData) {
        try {
            const subAccountData = { ...accountData };
            const { data } = await api.updateAccount({ accountId: this._id, subAccountData });
            runInAction(() => {
                this.account = { ...this.account, ...data };
            });
            return data;
        } catch (error) {
            console.error('Error updating account:', error);
            throw error;
        }
    }
}

export default AccountStore;